/**
 * 🖥 VDI 사이징 — 엑셀 「VDI」 시트 + 「Calculate」 (v2.233.0)
 *   유형 1. VDI 조건(데스크톱 수·IOPS·용량·RAM·클론·복제본·RAID·디스크·LUN 당 수) → 권장 구성·모델·부트 스톰 대책
 *   유형 2. 디스크 구성(종류·수·RAID) → 모델별 받을 수 있는 데스크톱 수 (용량은 안 본다)
 */
import { useState } from 'react';
import { calcVdi, calcVdiSupport } from '../../lib/sizing/calc';
import type { VdiInput } from '../../lib/sizing/calc';
import { VDI_DRIVE_CAPACITIES, VDI_DRIVE_LABEL, VDI_DRIVE_TYPES, VDI_MODELS, VDI_RAIDS, VDI_WORKERS } from '../../lib/sizing/data';
import type { VdiDriveType, VdiRaid } from '../../lib/sizing/types';
import { Field, Formula, NumInput, PAIR_GRID, Panel, SelectInput, SimpleTable, Stat, StatGrid, TRIO_GRID, TRIO_RESULT, fmt } from './SizingUi';

export function VdiCalc() {
  const [i, setI] = useState<VdiInput>({ desktops: 500, iopsPerDesktop: 10, desktopGb: 100, ramGb: 2, clone: 'Linked', replicaInStorage: true, raid: 'RAID 6', driveType: 'SAS15K', driveGb: 300, desktopsPerLun: 100 });
  const [t2, setT2] = useState<{ driveType: VdiDriveType; drives: number; raid: VdiRaid }>({ driveType: 'SAS15K', drives: 20, raid: 'RAID 5' });
  const caps = VDI_DRIVE_CAPACITIES[i.driveType];
  const driveGb = caps.includes(i.driveGb) ? i.driveGb : caps[0]!;
  const r = calcVdi({ ...i, driveGb });
  const s = calcVdiSupport(t2);
  const lunWarn = i.desktopsPerLun < 1 || i.desktopsPerLun > 140;

  return (
    <div className="space-y-8">
      <div>
        <h2 className="ui-modal-title mb-1">유형 1. VDI 조건을 넣고 권장 스토리지 구성 받기</h2>
        <p className="text-sm text-slate-500 mb-3">엑셀 안내: 데스크톱당 평균 IOPS 는 POC(시험 운영)로 재는 값 · 데스크톱 용량은 보통 25GB 를 넘지 않음 · 동시 접속률은 최악(100%)로만 계산.</p>
        {/* 넓은 PC 세 상자 나란히 · 중간 폭(lg)은 입력 둘 나란히 + 결과는 아래 한 줄 · 폰은 세로 (TRIO_GRID) */}
        <div className={TRIO_GRID}>
          <Panel title="가상 머신 조건" tone="input">
            <Field label="가상 데스크톱 수"><NumInput value={i.desktops} min={1} step={1} onChange={(desktops) => setI({ ...i, desktops })} suffix="대" /></Field>
            <Field label="데스크톱당 평균 IOPS" hint="정상 상태 · 부트 스톰은 5배로 본다"><NumInput value={i.iopsPerDesktop} min={1} step={1} onChange={(iopsPerDesktop) => setI({ ...i, iopsPerDesktop })} suffix="IOPS" /></Field>
            <Field label="동시 접속률" hint="엑셀은 100% 만 허용"><span className="text-base text-slate-500">100 % (고정)</span></Field>
            <Field label="데스크톱 용량"><NumInput value={i.desktopGb} min={1} step={1} onChange={(desktopGb) => setI({ ...i, desktopGb })} suffix="GB" /></Field>
            <Field label="데스크톱당 RAM" hint="스왑 공간으로 더해진다"><NumInput value={i.ramGb} min={1} step={1} onChange={(ramGb) => setI({ ...i, ramGb })} suffix="GB" /></Field>
            <Field label="클론 종류" hint="링크드 클론은 용량을 절반으로 본다"><SelectInput value={i.clone} options={[{ value: 'Linked', label: '링크드 클론 (Linked)' }, { value: 'Full', label: '풀 클론 (Full)' }]} onChange={(clone) => setI({ ...i, clone })} /></Field>
            <Field label="복제본(replica)을 스토리지에" hint="서버 SSD 에 두면 「아니오」"><SelectInput value={i.replicaInStorage ? 'yes' : 'no'} options={[{ value: 'yes', label: '예 — 스토리지에 둔다' }, { value: 'no', label: '아니오' }]} onChange={(v) => setI({ ...i, replicaInStorage: v === 'yes' })} /></Field>
          </Panel>
          <Panel title="스토리지 구성" tone="input">
            <Field label="RAID" hint="최고 성능은 RAID 10"><SelectInput value={i.raid} options={VDI_RAIDS.map((x) => ({ value: x, label: x }))} onChange={(raid) => setI({ ...i, raid })} /></Field>
            <Field label="디스크 종류"><SelectInput value={i.driveType} options={VDI_DRIVE_TYPES.map((x) => ({ value: x, label: VDI_DRIVE_LABEL[x] }))} onChange={(driveType) => setI({ ...i, driveType, driveGb: VDI_DRIVE_CAPACITIES[driveType][0]! })} /></Field>
            <Field label="디스크 용량"><SelectInput value={driveGb} options={caps.map((c) => ({ value: c, label: `${fmt(c)} GB` }))} onChange={(g) => setI({ ...i, driveGb: g })} /></Field>
            <Field label="LUN 당 데스크톱 수" hint="1~140"><NumInput value={i.desktopsPerLun} min={1} max={140} step={1} onChange={(desktopsPerLun) => setI({ ...i, desktopsPerLun })} suffix="대" /></Field>
            {lunWarn && <p className="text-sm text-red-600">LUN 당 데스크톱은 최대 140대입니다.</p>}
            <Field label="스페어 디스크" hint="엑셀은 입력만 받고 계산에 쓰지 않는다"><span className="text-base text-slate-500">계산에 반영 안 됨</span></Field>
          </Panel>
          <Panel title="권장 스토리지 구성" tone="result" className={TRIO_RESULT}>
            <StatGrid>
              <Stat label="권장 모델" value={r.models} big tone={r.models === '맞는 모델 없음' ? 'warn' : 'ok'} />
              <Stat label="총 구성 디스크 수" value={fmt(r.configuredDrives)} unit="개" big />
              <Stat label="LUN 수" value={fmt(r.luns)} unit="개" />
              <Stat label="LUN 당 디스크 수" value={fmt(r.drivesPerLun)} unit="개" />
              <Stat label="LUN 사용 가능 크기" value={fmt(r.usableLunGb)} unit="GB" />
              <Stat label="24베이 JBOD 수" value={fmt(r.jbod24)} unit="대" sub="첫 24개는 RAID 인클로저에" />
            </StatGrid>
            <div className="mt-3 text-sm">
              <div className={`rounded px-3 py-2 ${r.bootStormOk ? 'bg-emerald-100 text-emerald-800' : 'bg-amber-100 text-amber-900'}`}>
                {r.bootStormOk ? '✅ 지금 구성으로 부트 스톰(동시 부팅)을 감당할 수 있습니다.' : '⚠ 부트 스톰(동시 부팅)에는 SSD 논리 드라이브를 더 두는 것을 권합니다.'}
              </div>
              {r.ssdOptions && (
                <div className="mt-2">
                  <SimpleTable head={['부트 스톰용 SSD 옵션', 'SSD 수']} align={['l', 'r']} rows={[
                    ['옵션 1 — RAID 1', `${r.ssdOptions.raid1}개`], ['옵션 2 — RAID 5', `${r.ssdOptions.raid5}개`], ['옵션 3 — RAID 6', `${r.ssdOptions.raid6}개`],
                  ]} />
                </div>
              )}
            </div>
            <details className="mt-3 text-sm">
              <summary className="cursor-pointer text-slate-500 hover:text-slate-700">🔎 중간 계산</summary>
              <StatGrid className="mt-2">
                <Stat label="정상 상태 IOPS" value={fmt(r.steadyIops)} />
                <Stat label="부트 스톰 IOPS (× 5)" value={fmt(r.bootStormIops)} />
                <Stat label={`디스크 1개 IOPS (${VDI_DRIVE_LABEL[i.driveType]} · ${i.raid})`} value={fmt(r.iopsPerDisk)} />
                <Stat label="성능에 필요한 디스크 (정상 / 부트 스톰)" value={`${fmt(r.disksForPerf)} / ${fmt(r.disksForBootStorm)}`} />
                <Stat label="LUN 1개 용량" value={fmt(r.lunCapacityGb)} unit="GB" />
                <Stat label="전체 용량" value={fmt(r.totalCapacityGb)} unit="GB" />
                <Stat label="용량에 필요한 디스크 (디스크 + RAID 몫)" value={`${fmt(r.disksForCapacityRaw)} + ${fmt(r.raidReduction)} = ${fmt(r.disksForCapacity)}`} />
                <Stat label="필요한 디스크 (성능·용량 중 큰 것)" value={fmt(r.drivesRequired)} />
              </StatGrid>
            </details>
            <Formula>
              <p>LUN 수 = 올림(데스크톱 수 ÷ LUN 당 수) · 정상 IOPS = 데스크톱 × IOPS · 부트 스톰 = 정상 × 5</p>
              <p>LUN 용량 = LUN 당 데스크톱 × (용량(링크드면 × 0.5) + RAM) × 1.2 · 복제본을 두면 LUN 마다 데스크톱 용량 × 2 를 더함</p>
              <p>용량용 디스크 = 올림(전체 ÷ 디스크 GB) + RAID 몫(RAID 5 는 LUN 수 · RAID 6 은 LUN 수 × 2 · RAID 10 은 같은 수만큼)</p>
              <p>필요 디스크 = 성능(정상)·용량 중 큰 값 → LUN 당 = 올림(÷ LUN 수) (RAID 10 은 짝수) → 구성 = LUN 수 × LUN 당</p>
              <p>모델 = 부트 스톰 IOPS(47k · 75k · 167k · 231k)와 디스크 수(360 · 444)로 가름 · LUN 사용 가능 = (LUN 당 디스크 − 패리티) × 디스크 GB</p>
            </Formula>
          </Panel>
        </div>
      </div>

      <div>
        <h2 className="ui-modal-title mb-1">유형 2. 디스크 구성을 넣고 받을 수 있는 데스크톱 수 보기 (용량은 안 봄)</h2>
        <div className={PAIR_GRID}>
          <Panel title="구성" tone="input">
            <Field label="디스크 종류"><SelectInput value={t2.driveType} options={VDI_DRIVE_TYPES.map((x) => ({ value: x, label: VDI_DRIVE_LABEL[x] }))} onChange={(driveType) => setT2({ ...t2, driveType })} /></Field>
            <Field label="디스크 수" hint="2~444"><NumInput value={t2.drives} min={2} max={444} step={1} onChange={(drives) => setT2({ ...t2, drives })} suffix="개" /></Field>
            <Field label="RAID"><SelectInput value={t2.raid} options={VDI_RAIDS.map((x) => ({ value: x, label: x }))} onChange={(raid) => setT2({ ...t2, raid })} /></Field>
            {s.perDisk === null && <p className="text-sm text-red-600 mt-1">이 RAID 로는 이 디스크 수를 쓸 수 없습니다 (RAID 10 은 짝수 · RAID 5 는 3개 이상 · RAID 6 은 4개 이상).</p>}
          </Panel>
          <Panel title="모델별 지원 데스크톱 수" tone="result">
            <StatGrid>
              <Stat label="디스크 1개 IOPS" value={s.perDisk === null ? '—' : fmt(s.perDisk)} />
              <Stat label="총 IOPS" value={fmt(s.totalIops)} big />
            </StatGrid>
            <div className="mt-3">
              <SimpleTable
                wrap
                head={['작업자 유형', ...VDI_MODELS.map((m) => <span key={m.name}>{m.name}<br /><span className="font-normal text-xs text-slate-500">{m.spec}</span></span>)]}
                align={['l', 'r', 'r', 'r', 'r']}
                rows={VDI_WORKERS.map((w) => [
                  <span key={w.key}>{w.label}<span className="text-slate-400"> · {w.iops} IOPS</span></span>,
                  ...s.byModel.map((m) => { const v = m[w.key]; return v === null ? <span className="text-slate-400">N/A</span> : `${fmt(v)}대`; }),
                ])}
              />
            </div>
            <Formula>
              <p>총 IOPS = 디스크 1개 IOPS × 디스크 수 · 지원 수 = 올림(총 IOPS ÷ 작업자 IOPS) — 모델 최대 IOPS(4000U 231k · 3000T 167k · 2000 75k · 1000T 47k)를 넘으면 최대 ÷ 작업자 IOPS</p>
              <p>디스크가 360개를 넘으면 4000U(444개까지)만 가능</p>
            </Formula>
          </Panel>
        </div>
      </div>
    </div>
  );
}
